/**
 * 🎯 DealListView — /deals
 *
 * P7-3 — Deal Operations Dashboard 목록 페이지.
 *
 * 데이터 흐름:
 *   GET /api/admin/deal?... → v_deal_list JOIN 컬럼 포함 (customer_name, assignee_name)
 *
 * 필터:
 *   - 진행중 (open_only=true)
 *   - 완료 (status=납품 완료)
 *   - 취소 (status=취소)
 *   - 전체
 *   - 미배정 (unassigned_only=true)
 *
 * 행 클릭 → DealDetailDrawer.open(id) — P7-4 가 본 작업.
 *
 * 정렬 default: updated_at DESC (가장 최근 활동 위로).
 */

import { SolaView, htmlSolaWithInstance, navigate } from '@hm-spt/sola';
import * as api from '../api/client';
// DealDetailDrawer — 옛 drawer 컴포넌트. P9-UI Step 1 에서 새 탭 페이지(/deal/:id) 로 이전.
// 코드 자체는 보존 (필요 시 archive). 여기서는 import 제거.
import { escapeHtml, fmtDate } from '../lib/cells';
import { statusBadgeStyle } from '../lib/deal-status-color';
import { hasTextSelection } from '../lib/selection';

const STATUS_LIST: api.DealStatus[] = [
  '리드 접수됨', '리드 검토됨', '소싱 확인중', '견적 작성 완료', '견적 발송 완료',
  '수주', '매입 진행중', '세팅중', '세팅 완료', '납품 완료', '취소',
];
const CLASSIFICATION_LIST: api.DealClassification[] = ['파트 구매', '파트 임대', '서버 구매', '서버 임대'];

interface DealListData {
  rows:     api.DealRow[];
  total:    number;
  page:     number;         // 현재까지 로드한 페이지(무한스크롤)
  loadingMore: boolean;     // 다음 페이지 로드 중
  loading:  boolean;
  errorMessage: string;
  search:   string;
  assignees: api.UserAssignee[];      // 영업담당자 셀렉터 옵션
  fStatus:         api.DealStatus | '';
  fAssignee:       string;            // users.id 또는 ''
  fClassification: api.DealClassification | '';
  fWatched:        boolean;           // ★ 관심만
}

// 상태 badge 컬러는 단일 소스(deal-status-color.ts) 인라인 스타일로 적용.

export class DealListView extends SolaView<DealListData> {
  // drawer 제거됨 (P9-UI Step 1) — 새 탭 페이지 /deal/:id 로 전환

  override events = {
    click: {
      // 내부(셀 버튼) 액션을 @row 보다 먼저 — Sola first-match-wins 라 순서 중요
      '@watch':        'handleWatchToggle',
      '@copy-no':      'handleCopyDealNo',
      '@watch-filter': 'handleWatchFilter',
      '@row':          'handleRowClick',
      '@search':       'handleSearchClick',
      '@create-deal':  'handleCreateDeal',
    },
    keydown: {
      '@search-input': 'handleSearchKey',
    },
    input: {
      '@search-input': 'handleSearchInput',
    },
    change: {
      '@filter-status':         'handleFilterStatus',
      '@filter-assignee':       'handleFilterAssignee',
      '@filter-classification': 'handleFilterClassification',
    },
  };

  /** 검색 라이브 디바운스 타이머 */
  private _searchTimer: number | undefined;

  constructor() {
    super();
    this.data = {
      rows:         [],
      total:        0,
      page:         1,
      loadingMore:  false,
      loading:      true,
      errorMessage: '',
      search:       '',
      assignees:    [],
      fStatus:         '',
      fAssignee:       '',
      fClassification: '',
      fWatched:        false,
    };
  }

  override getTitle(): string {
    return '딜 — HCLTech';
  }

  override onMount(): void {
    void this.loadRows();
    void this.loadAssignees();
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }

  override onUnmount(): void {
    window.removeEventListener('scroll', this._onScroll);
    if (this._searchTimer) window.clearTimeout(this._searchTimer);
    super.onUnmount();
  }

  // ─── 무한 스크롤 (window scroll — 소싱과 동일 패턴) ─────────────────
  private _onScroll = (): void => {
    if (this.data.loading || this.data.loadingMore) return;
    if (this.data.rows.length >= this.data.total) return;    // 다 로드됨
    const nearBottom = window.innerHeight + window.scrollY
      >= document.documentElement.scrollHeight - 400;
    if (nearBottom) void this.loadMore();
  };

  private async loadMore(): Promise<void> {
    if (this.data.loadingMore || this.data.rows.length >= this.data.total) return;
    this.data.loadingMore = true;
    this.data.page += 1;
    this.rerenderBody();
    try {
      const r = await api.fetchDealList(this.buildQuery());
      this.data.rows  = [...this.data.rows, ...r.rows];
      this.data.total = r.total;                             // 그 사이 변동 반영
    } catch {
      this.data.page -= 1;                                   // 실패 시 페이지 롤백
    }
    this.data.loadingMore = false;
    this.rerenderBody();
  }

  private async loadAssignees(): Promise<void> {
    try {
      this.data.assignees = await api.fetchActiveAssignees();
      this.rerenderFilters();
    } catch { /* 셀렉터는 비어도 동작 */ }
  }

  // ─── data ──────────────────────────────────────────────────────────
  private buildQuery(): api.DealListQuery {
    const q: api.DealListQuery = {
      // 상태 우선순위(수주→…→리드접수됨→이행→취소) → 같은 상태는 updated_at 최신순.
      //   백엔드가 sort='status' 일 때 DEAL_STATUS_SORT_ORDER 기준 CASE 정렬.
      sort:     'status',
      order:    'desc',
      page:     this.data.page,
      pageSize: 50,
    };
    if (this.data.search)         q.search           = this.data.search;
    if (this.data.fStatus)        q.status           = this.data.fStatus;
    if (this.data.fAssignee)      q.assignee_user_id = Number(this.data.fAssignee);
    if (this.data.fClassification) q.classification  = this.data.fClassification;
    if (this.data.fWatched)       q.watched          = 'true';
    return q;
  }

  private async loadRows(): Promise<void> {
    this.data.loading = true;
    this.data.page    = 1;                 // 필터/검색/최초 로드 = 첫 페이지로 리셋
    this.setLoadingBanner(true);
    try {
      const r = await api.fetchDealList(this.buildQuery());
      this.data.rows  = r.rows;
      this.data.total = r.total;
      this.data.errorMessage = '';
    } catch (err) {
      this.data.errorMessage = `딜 목록 로드 실패: ${(err as Error).message}`;
      this.data.rows  = [];
      this.data.total = 0;
    } finally {
      this.data.loading = false;
      this.rerenderBody();
    }
  }

  // ─── 이벤트 핸들러 ─────────────────────────────────────────────────
  handleFilterStatus(_e: Event, target: HTMLElement): void {
    this.data.fStatus = (target as HTMLSelectElement).value as api.DealStatus | '';
    void this.loadRows();
  }
  handleFilterAssignee(_e: Event, target: HTMLElement): void {
    this.data.fAssignee = (target as HTMLSelectElement).value;
    void this.loadRows();
  }
  handleFilterClassification(_e: Event, target: HTMLElement): void {
    this.data.fClassification = (target as HTMLSelectElement).value as api.DealClassification | '';
    void this.loadRows();
  }
  handleWatchFilter(_e: Event, target: HTMLElement): void {
    this.data.fWatched = !this.data.fWatched;
    target.classList.toggle('is-on', this.data.fWatched);
    target.setAttribute('aria-pressed', this.data.fWatched ? 'true' : 'false');
    void this.loadRows();
  }

  handleRowClick(e: Event, target: HTMLElement): void {
    if (hasTextSelection()) return;   // 긁어 복사(드래그)면 안 연다 — 클릭과 가른다(2026-10-30 전수)
    // 별표/복사 버튼 클릭은 행 이동에서 제외
    if ((e.target as HTMLElement).closest('[data-action="watch"], [data-action="copy-no"]')) return;
    const id = target.closest('[data-deal-id]')?.getAttribute('data-deal-id');
    if (!id) return;
    // P9-UI Step 1: drawer → 새 탭의 상세 페이지
    window.open(`/deal/${encodeURIComponent(id)}`, '_blank', 'noopener');
  }

  // Watchlist 별표 토글 (개인 설정 — deal.updated_at 미갱신). 낙관적 갱신.
  async handleWatchToggle(_e: Event, target: HTMLElement): Promise<void> {
    const btn = target.closest<HTMLElement>('[data-deal-id]');
    const id  = btn?.getAttribute('data-deal-id');
    if (!id || !btn) return;
    const row = this.data.rows.find((d) => d.id === id);
    if (!row) return;
    const next = !row.is_watched;
    row.is_watched = next;
    // 별표 DOM 즉시 반영
    btn.classList.toggle('is-on', next);
    btn.textContent = next ? '★' : '☆';
    btn.setAttribute('aria-pressed', next ? 'true' : 'false');
    btn.setAttribute('title', next ? '관심 해제' : '관심 등록');
    try {
      await api.setDealWatch(id, next);
      // '관심' 필터 중이고 해제했으면 목록에서 빠지도록 새로고침
      if (!next && this.data.fWatched) void this.loadRows();
    } catch (err) {
      row.is_watched = !next;   // 롤백
      btn.classList.toggle('is-on', !next);
      btn.textContent = !next ? '★' : '☆';
      alert(`관심 설정 실패: ${(err as Error).message}`);
    }
  }

  async handleCopyDealNo(_e: Event, target: HTMLElement): Promise<void> {
    const no = target.getAttribute('data-deal-no');
    if (!no) return;
    try {
      await navigator.clipboard.writeText(no);
      target.textContent = '✓';
      setTimeout(() => { target.textContent = '⧉'; }, 1000);
    } catch {
      /* 클립보드 차단 환경 — 무시 */
    }
  }

  handleSearchInput(_e: Event, target: HTMLElement): void {
    this.data.search = (target as HTMLInputElement).value;
    // 라이브 검색 — 300ms 디바운스 후 서버 조회
    if (this._searchTimer) window.clearTimeout(this._searchTimer);
    this._searchTimer = window.setTimeout(() => { void this.loadRows(); }, 300);
  }

  handleSearchKey(e: Event, _target: HTMLElement): void {
    if ((e as KeyboardEvent).key === 'Enter') {
      if (this._searchTimer) window.clearTimeout(this._searchTimer);
      void this.loadRows();
    }
  }

  handleSearchClick(): void {
    void this.loadRows();
  }

  // 딜 생성 — 빈 드래프트 딜을 만들고 생성 페이지(딜 상세, 블랭크+채팅)로 이동.
  private _creating = false;
  async handleCreateDeal(_e: Event, target: HTMLElement): Promise<void> {
    if (this._creating) return;
    this._creating = true;
    const btn = target as HTMLButtonElement;
    btn.disabled = true;
    // 새 탭으로 열기 — await 뒤 window.open 은 팝업 차단에 걸리므로 클릭 제스처 내에서
    //   빈 탭을 먼저 열고, 딜 생성 후 URL 을 지정한다(차단 시 현재 탭 이동 폴백).
    const tab = window.open('about:blank', '_blank');
    try {
      const { id } = await api.createDraftDeal();
      const url = `/deals/new/${encodeURIComponent(id)}`;
      if (tab) { try { tab.opener = null; } catch { /* noop */ } tab.location.href = url; }
      else navigate(url);
    } catch (err) {
      tab?.close();
      alert(`딜 생성 실패: ${err instanceof Error ? err.message : 'unknown'}`);
    } finally {
      btn.disabled = false;
      this._creating = false;
    }
  }

  // ─── 렌더 ──────────────────────────────────────────────────────────
  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-deal-list">
        <header class="page-header">
          <div>
            <h1>🤝 딜</h1>
            <p class="page-subtitle">Slack 자동 등록 + 관리자 운영 — 영업 진행 현황 한눈에</p>
          </div>
          <button class="btn btn-primary" type="button" data-action="create-deal">딜 생성</button>
        </header>

        <div class="filter-bar">
          <input
            class="filter-search"
            type="search"
            placeholder="검색 대상 : 딜 넘버, 딜 이름, 파트넘버, 디스크립션"
            data-action="search-input"
            value="${escapeHtml(this.data.search)}"
          />
          <button class="deal-watch-toggle${this.data.fWatched ? ' is-on' : ''}" type="button"
                  data-action="watch-filter" aria-pressed="${this.data.fWatched ? 'true' : 'false'}"
                  title="관심(별표) 딜만">★</button>
          <div class="deal-filter-selects">${this.renderFilterSelects()}</div>
        </div>

        <div class="banner-loading hidden">목록 불러오는 중…</div>
        <div class="banner-error hidden" data-bind="errorMessage">${escapeHtml(this.data.errorMessage)}</div>

        <section class="grid-wrap">
          ${this.renderTable()}
        </section>

        <p class="list-footer text-muted">총 ${this.data.total} 건</p>
      </div>
    `;
  }

  private renderTable(): string {
    if (this.data.rows.length === 0 && !this.data.loading) {
      return `<p class="empty">조건에 맞는 딜이 없습니다.</p>`;
    }
    // 최근 수정일 기준 daily 그룹 (§2.3.8) — rows 는 updated_at DESC 정렬
    // 그룹별 건수 미리 집계
    const counts = new Map<string, number>();
    for (const d of this.data.rows) {
      const k = fmtDate(d.updated_at);
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    let body = '';
    let lastDate = '';
    for (const d of this.data.rows) {
      const dateLabel = fmtDate(d.updated_at);
      if (dateLabel !== lastDate) {
        body += `<tr class="deal-group-row"><td colspan="9">
          <span class="deal-group-date">${escapeHtml(dateLabel)}</span>
          <span class="deal-group-count">· ${counts.get(dateLabel)}건</span>
        </td></tr>`;
        lastDate = dateLabel;
      }
      body += this.renderRow(d);
    }
    return `
      <table class="grid-table grid-table-clickable deal-list-table">
        <thead>
          <tr>
            <th>딜 넘버</th>
            <th>딜 이름</th>
            <th>업체명</th>
            <th>담당자명</th>
            <th>상태</th>
            <th>문의 분류</th>
            <th>영업담당자</th>
            <th class="num">견적금액</th>
            <th class="deal-watch-col" aria-label="관심"></th>
          </tr>
        </thead>
        <tbody>${body}</tbody>
      </table>
    `;
  }

  private renderRow(d: api.DealRow): string {
    const no = d.deal_no ?? `#${d.id}`;
    return `
      <tr data-action="row" data-deal-id="${escapeHtml(d.id)}">
        <td class="deal-no-cell">
          <code>${escapeHtml(no)}</code>
          <button class="deal-copy-btn" type="button" data-action="copy-no"
                  data-deal-no="${escapeHtml(no)}" title="딜 넘버 복사" aria-label="복사">⧉</button>
        </td>
        <td><div class="deal-row-name">${escapeHtml(d.name)}</div></td>
        <td>${escapeHtml(d.customer_name ?? '-')}</td>
        <td>${escapeHtml(d.contact_name ?? '-')}</td>
        <td><span class="chip" style="${statusBadgeStyle(d.status)}">${escapeHtml(d.status)}</span></td>
        <td>${d.classification ? escapeHtml(d.classification) : '<span class="text-muted">-</span>'}</td>
        <td>${d.assignee_name ? escapeHtml(d.assignee_name) : '<span class="text-muted">미배정</span>'}</td>
        <td class="num">${this.fmtQuoteAmount(d.quote_amount_krw)}</td>
        <td class="text-center deal-watch-col">
          <button class="deal-watch-star${d.is_watched ? ' is-on' : ''}" type="button"
                  data-action="watch" data-deal-id="${escapeHtml(d.id)}"
                  aria-label="관심" aria-pressed="${d.is_watched ? 'true' : 'false'}"
                  title="${d.is_watched ? '관심 해제' : '관심 등록'}">${d.is_watched ? '★' : '☆'}</button>
        </td>
      </tr>
    `;
  }

  // 견적금액 — 발송/채택된 견적이 있을 때만 금액, 없으면 '견적 작성 전' (§2.2.11-12)
  private fmtQuoteAmount(amount: number | null | undefined): string {
    if (amount == null) return '<span class="text-muted">견적 작성 전</span>';
    return `₩ ${amount.toLocaleString('ko-KR')}`;
  }

  // ─── 부분 갱신 헬퍼 ────────────────────────────────────────────────
  private rerenderBody(): void {
    const root = this._rootElement;
    if (!root) return;
    const wrap = root.querySelector('.grid-wrap');
    if (wrap) wrap.innerHTML = this.renderTable();
    this.setLoadingBanner(this.data.loading);
    const banner = root.querySelector<HTMLElement>('.banner-error');
    if (banner) {
      if (this.data.errorMessage) {
        banner.textContent = this.data.errorMessage;
        banner.classList.remove('hidden');
      } else {
        banner.classList.add('hidden');
      }
    }
    const footer = root.querySelector<HTMLElement>('.list-footer');
    if (footer) {
      const more = this.data.rows.length < this.data.total;
      footer.textContent = this.data.loadingMore
        ? `총 ${this.data.total} 건 · 불러오는 중…`
        : more
          ? `총 ${this.data.total} 건 · ${this.data.rows.length} 표시 (스크롤해 더 보기)`
          : `총 ${this.data.total} 건`;
    }
  }

  private setLoadingBanner(on: boolean): void {
    const el = this._rootElement?.querySelector<HTMLElement>('.banner-loading');
    if (!el) return;
    if (on) el.classList.remove('hidden');
    else el.classList.add('hidden');
  }

  // ─── 필터 셀렉터 ───────────────────────────────────────────────────
  private renderFilterSelects(): string {
    const statusOpts = `<option value="">전체 상태</option>` +
      STATUS_LIST.map((s) =>
        `<option value="${escapeHtml(s)}"${this.data.fStatus === s ? ' selected' : ''}>${escapeHtml(s)}</option>`).join('');
    const assigneeOpts = `<option value="">전체 영업담당자</option>` +
      this.data.assignees.map((a) =>
        `<option value="${escapeHtml(a.id)}"${this.data.fAssignee === a.id ? ' selected' : ''}>${escapeHtml(a.name)}</option>`).join('');
    const classOpts = `<option value="">전체 문의 분류</option>` +
      CLASSIFICATION_LIST.map((c) =>
        `<option value="${escapeHtml(c)}"${this.data.fClassification === c ? ' selected' : ''}>${escapeHtml(c)}</option>`).join('');
    return `
      <select class="deal-filter-select" data-action="filter-status" aria-label="상태">${statusOpts}</select>
      <select class="deal-filter-select" data-action="filter-assignee" aria-label="영업담당자">${assigneeOpts}</select>
      <select class="deal-filter-select" data-action="filter-classification" aria-label="문의 분류">${classOpts}</select>
    `;
  }

  private rerenderFilters(): void {
    const wrap = this._rootElement?.querySelector('.deal-filter-selects');
    if (wrap) wrap.innerHTML = this.renderFilterSelects();
  }
}
